import React, { useState, useEffect, useRef } from 'react';
import { SessionStatus } from '../services/sessionStatus';
import { DELAY_REASON_LABELS } from '../services/types';

interface StatusBadgeProps {
  status: number;
  delayReason?: string | null;
  delayCmoName?: string | null;
  delayDetails?: string | null;
  delayActions?: string | null;
}

const STATUS_CONFIG: Record<number, { label: string; bg: string; text: string }> = {
  [SessionStatus.Draft]: { label: 'In Progress', bg: 'bg-blue-100', text: 'text-blue-700' },
  [SessionStatus.InProgress]: { label: 'In Progress', bg: 'bg-blue-100', text: 'text-blue-700' },
  [SessionStatus.Submitted]: { label: 'Pending QP', bg: 'bg-amber-100', text: 'text-amber-700' },
  [SessionStatus.Completed]: { label: 'Completed', bg: 'bg-green-100', text: 'text-green-700' },
  [SessionStatus.Rejected]: { label: 'Rejected', bg: 'bg-red-100', text: 'text-red-700' },
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, delayReason, delayCmoName, delayDetails, delayActions }) => {
  const [showPopover, setShowPopover] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!showPopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopover]);

  const config = STATUS_CONFIG[status] || { label: 'Unknown', bg: 'bg-gray-100', text: 'text-gray-500' };
  const isDelayed = (status === SessionStatus.Draft || status === SessionStatus.InProgress) && !!delayReason;

  if (!isDelayed) {
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  }

  return (
    <span ref={wrapperRef} className="relative inline-flex items-center gap-1">
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
        Delayed
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); setShowPopover(!showPopover); }}
        className="text-gray-400 hover:text-gray-600"
        style={{ width: 16, height: 16, borderRadius: '50%', border: '1px solid currentColor', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, fontStyle: 'italic', lineHeight: 1 }}
      >
        i
      </button>
      {showPopover && (
        <div
          className="absolute z-20 left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-left"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-gray-700">Delay Details</span>
            <button onClick={() => setShowPopover(false)} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
          </div>
          <p className="text-xs text-gray-600 mb-1">
            <span className="font-semibold">Reason:</span> {DELAY_REASON_LABELS[delayReason!] || delayReason}
          </p>
          {delayCmoName && <p className="text-xs text-gray-600 mb-1"><span className="font-semibold">CMO:</span> {delayCmoName}</p>}
          {delayDetails && <p className="text-xs text-gray-600 mb-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{delayDetails}</p>}
          {delayActions && (
            <>
              <span className="text-xs font-semibold text-gray-700">Actions Being Taken</span>
              <p className="text-xs text-gray-600 mt-0.5" style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{delayActions}</p>
            </>
          )}
        </div>
      )}
    </span>
  );
};
